import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H6 } from 'foundations-components/transitional/components/H6';
import { H2 } from 'foundations-components/transitional/components/H2';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { ScrollingColumns } from 'foundations-components/transitional/components/ScrollingColumns';
import { createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
import { useSnowflakeReportUpdater } from 'reporting-snowflake/relational/updaters/report/use-snowflake-updater';
import { getReportColumnsFromRelationalReport } from 'report-customization-components/lib/relational-report-utils/property-utils';
import RelationalReportPropertyOptionsPanel from './RelationalReportPropertyOptionsPanel';
import RelationalReportSelectedPropertiesPanel from './RelationalReportSelectedPropertiesPanel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DataColumnsButton = ({
  selectedPropertiesCount,
  setIsModalOpen
}) => {
  return /*#__PURE__*/_jsx(Button, {
    use: "tertiary",
    size: "extra-small",
    onClick: () => {
      setIsModalOpen(true);
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "report-customization-components.customization-components.data-columns-modal.config.selectedProperties",
      options: {
        count: selectedPropertiesCount
      }
    })
  });
};
const RelationalReportDataColumnsModal = ({
  report,
  onChange
}) => {
  const [localReport, onSetLocalReport] = useState(report);
  const localRelationalReport = useMemo(() => createRelationalReportFromBaseReport(localReport), [localReport]);
  const localRelationalUpdater = useSnowflakeReportUpdater(localRelationalReport);
  const {
    allAvailableColumns: localAllAvailableColumns,
    currentAllColumns: localCurrentAllColumns
  } = useMemo(() => {
    return getReportColumnsFromRelationalReport(localRelationalReport);
  }, [localRelationalReport]);
  const selectedPropertiesCount = localAllAvailableColumns.size;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const onCloseModal = () => {
    setIsModalOpen(false);
    onSetLocalReport(report);
  };
  useEffect(() => {
    onSetLocalReport(report);
  }, [report]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isModalOpen && /*#__PURE__*/_jsxs(Modal, {
      width: 850,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "report-customization-components.customization-components.data-columns-modal.config.label"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onCloseModal
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsx(ScrollingColumns, {
          leftColumnHeader: /*#__PURE__*/_jsx(SearchInput, {
            placeholder: I18n.text('report-customization-components.customization-components.data-columns-modal.config.selectProperties'),
            onChange: ({
              target: {
                value: updatedValue
              }
            }) => {
              setSearch(updatedValue);
            },
            value: search
          }),
          leftColumnContent: /*#__PURE__*/_jsx(RelationalReportPropertyOptionsPanel, {
            report: localReport,
            onChange: onSetLocalReport,
            searchValue: deferredSearch,
            localRelationalReport: localRelationalReport,
            localRelationalUpdater: localRelationalUpdater,
            localAllAvailableColumns: localAllAvailableColumns,
            localCurrentAllColumns: localCurrentAllColumns
          }),
          rightColumnHeader: /*#__PURE__*/_jsx(H6, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "report-customization-components.customization-components.data-columns-modal.config.selectedProperties",
              options: {
                count: selectedPropertiesCount
              }
            })
          }),
          rightColumnContent: /*#__PURE__*/_jsx(RelationalReportSelectedPropertiesPanel, {
            report: localReport,
            onChange: onSetLocalReport,
            localRelationalUpdater: localRelationalUpdater,
            localAllAvailableColumns: localAllAvailableColumns,
            localCurrentAllColumns: localCurrentAllColumns
          })
        })
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          onClick: () => {
            onChange(localReport);
            setIsModalOpen(false);
          },
          type: "submit",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "report-customization-components.customization-components.data-columns-modal.config.apply"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: onCloseModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "report-customization-components.customization-components.data-columns-modal.config.cancel"
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(HelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.data-columns-modal.config.tooltip"
      }),
      children: /*#__PURE__*/_jsx(DataColumnsButton, {
        selectedPropertiesCount: selectedPropertiesCount,
        setIsModalOpen: setIsModalOpen
      })
    })]
  });
};
export default RelationalReportDataColumnsModal;